import { useState } from 'react';

/**
 * BlogTabs — reusable tabbed panel for in-post interactive items.
 *
 * Blog figures that carry several parallel artifacts (one per benchmark arm)
 * do not fit a chart: the reader wants to switch between them, not see them
 * side by side. This is that switcher — a labelled tab strip plus one panel.
 *
 * Generic on purpose (RULES_BLOG_POST Rule 29: variants are parameters of one
 * component, never duplicated files). `accent` marks the tab that is the
 * subject of the post so it reads differently from its comparisons — identity
 * is never colour-alone, the tab is also labelled.
 */

export interface BlogTab {
  key: string;
  label: string;
  /** Renders with the brand accent instead of neutral (the post's subject). */
  accent?: boolean;
  content: React.ReactNode;
}

export const BlogTabs = ({
  tabs,
  caption,
  initial,
}: {
  tabs: BlogTab[];
  caption?: React.ReactNode;
  /** Tab key to open first. Defaults to the first tab. */
  initial?: string;
}) => {
  const [active, setActive] = useState(initial ?? tabs[0]?.key);
  const current = tabs.find((t) => t.key === active) ?? tabs[0];

  return (
    <div className="my-8 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
      {/* Phones: one scrollable row instead of a wrapped block. Five labels like
          "Professional System Prompt" wrap to ~5 lines in a ~343px column, which
          pushes the panel below the fold before the reader sees any content.
          `snap-x` keeps a tab aligned after a swipe; `shrink-0` stops the flex
          row from squeezing labels into unreadable slivers. */}
      <div
        role="tablist"
        className="flex gap-1 overflow-x-auto sm:flex-wrap sm:overflow-visible snap-x border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/40 p-1.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {tabs.map((t) => {
          const on = t.key === current?.key;
          return (
            <button
              key={t.key}
              role="tab"
              aria-selected={on}
              onClick={() => setActive(t.key)}
              className={[
                // min-h-8 keeps the tap target usable on touch.
                'shrink-0 snap-start rounded-lg px-3 py-1.5 min-h-8 text-label-xs sm:text-xs font-medium transition-colors',
                on
                  ? t.accent
                    ? 'bg-cyan-600 text-white'
                    : 'bg-slate-700 text-white dark:bg-slate-200 dark:text-slate-900'
                  : t.accent
                    ? 'text-cyan-700 dark:text-cyan-400 hover:bg-cyan-50 dark:hover:bg-cyan-950/30'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-200/60 dark:hover:bg-slate-800',
              ].join(' ')}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      <div role="tabpanel" className="p-3 sm:p-4">
        {current?.content}
      </div>

      {caption && (
        <p className="px-3 sm:px-4 pb-4 -mt-1 text-label-xs sm:text-xs text-slate-500 dark:text-slate-400 text-center">
          {caption}
        </p>
      )}
    </div>
  );
};
